import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, Users, Target, TrendingUp, CheckCircle, Menu, X, GraduationCap, Briefcase, BarChart3, Handshake, Award, Quote, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const LanguageSwitcher = () => {
  const { lang, switchLanguage } = useLanguage();
  return (
    <div className="language-switcher" data-testid="language-switcher">
      <button
        onClick={() => switchLanguage('fr')}
        className={`lang-flag ${lang === 'fr' ? 'active' : ''}`}
        data-testid="lang-fr-btn"
        title="Français"
        aria-label="Français"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" width="28" height="19">
          <rect width="900" height="600" fill="#ED2939"/>
          <rect width="600" height="600" fill="#fff"/>
          <rect width="300" height="600" fill="#002395"/>
        </svg>
      </button>
      <button
        onClick={() => switchLanguage('en')}
        className={`lang-flag ${lang === 'en' ? 'active' : ''}`}
        data-testid="lang-en-btn"
        title="English"
        aria-label="English"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="28" height="19">
          <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
          <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
          <g clipPath="url(#s)">
            <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
            <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
          </g>
        </svg>
      </button>
    </div>
  );
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'reality', 'clients', 'results', 'framework', 'vision', 'accompagnements', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const handleNavClick = (item) => {
    if (item.isExternal) {
      navigate('/articles');
    } else {
      scrollToSection(item.id);
    }
  };

  const navItems = [
    { id: 'hero', label: t.nav.home },
    { id: 'vision', label: t.nav.vision },
    { id: 'accompagnements', label: t.nav.services },
    { id: 'articles', label: t.nav.articles, isExternal: true },
    { id: 'contact', label: t.nav.contact }
  ];

  const allClientIcons = [Target, Users, TrendingUp, Briefcase, Users];
  const clientIcons = allClientIcons.slice(0, t.clients.cards.length);

  const serviceImages = [
    '/images/coaching/coaching-executive.jpg',
    '/images/coaching/coaching-industry.jpg',
    '/images/coaching/coaching-startup.jpg',
  ];

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="fixed-nav">
        <div className="nav-content">
          <div className="nav-logo">
            <span className="logo-text">{t.nav.logoText}</span>
          </div>

          {/* Desktop Navigation */}
          <div className="nav-links-desktop">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="mobile-controls">
            <LanguageSwitcher />
            <button
              className="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="nav-links-mobile">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`nav-link-mobile ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-image" style={{ backgroundImage: `url(/images/coaching/coaching-executive.jpg)` }}></div>
        <div className="hero-content">
          <div className="hero-layout">
            <div className="hero-text-side">
              <h1 className="hero-title">
                {t.hero.title}
                {t.hero.titleHighlight && (<><br /><span className="hero-title-highlight">{t.hero.titleHighlight}</span></>)}
              </h1>
              <div className="hero-statements">
                <p className="hero-statement">{t.hero.statement1.pre}<strong>{t.hero.statement1.bold1}</strong>{t.hero.statement1.mid}<strong>{t.hero.statement1.bold2}</strong>{t.hero.statement1.post}</p>
                <p className="hero-statement">{t.hero.statement2.pre}<strong>{t.hero.statement2.bold1}</strong>{t.hero.statement2.post}</p>
                <p className="hero-statement">{t.hero.statement3.pre}<strong>{t.hero.statement3.bold1}</strong>{t.hero.statement3.post}</p>
              </div>
              <p className="hero-conclusion">
                {t.hero.conclusionPre}<strong>{t.hero.conclusionBold}</strong>{t.hero.conclusionDot}<br />
                <span className="hero-emphasis">{t.hero.emphasis}</span>
              </p>
              <Button 
                size="lg" 
                className="hero-cta"
                onClick={() => scrollToSection('contact')}
                data-testid="hero-cta-btn"
              >
                {t.hero.cta}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
            
            <div className="hero-photo-side">
              <div className="hero-profile-container">
                <img 
                  src="/images/loic-blandel-profile.jpg" 
                  alt={t.hero.profileAlt}
                  className="hero-profile-photo"
                />
              </div>
            </div>
          </div>
          
          <div className="hero-manifesto">
            <div className="manifesto-label">{t.hero.manifestoLabel}</div>
            <p className="manifesto-emphasis-large">
              {t.hero.manifestoText}<strong>{t.hero.manifestoBold}</strong>{t.hero.manifestoPost}
            </p>
          </div>
        </div>
      </section>

      {/* Reality Section */}
      <section id="reality" className="section-container section-reality">
        <div className="section-content">
          <div className="section-label">{t.reality.label}</div>
          <h2 className="section-title">{t.reality.title}</h2>
          <div className="reality-grid">
            <div className="reality-statement">
              <p className="statement-text">{t.reality.s1.pre}<strong>{t.reality.s1.bold}</strong>{t.reality.s1.post}</p>
              <p className="statement-text">{t.reality.s2.pre}<strong>{t.reality.s2.bold}</strong>{t.reality.s2.post}</p>
              {(t.reality.s3.pre || t.reality.s3.bold) && <p className="statement-text">{t.reality.s3.pre}<strong>{t.reality.s3.bold}</strong>{t.reality.s3.post}</p>}
              <p className="statement-text">{t.reality.s4.pre}<strong>{t.reality.s4.bold}</strong>{t.reality.s4.post}</p>
              {(t.reality.s5.pre || t.reality.s5.bold) && <p className="statement-text">{t.reality.s5.pre}<strong>{t.reality.s5.bold}</strong>{t.reality.s5.post}</p>}
            </div>
            <Separator className="my-8" />
            <div className="reality-tensions">
              {t.reality.tensionsTitle && <h3 className="tensions-title">{t.reality.tensionsTitle}</h3>}
              <ul className="tensions-list">
                {t.reality.tensions.map((tension, i) => (
                  <li key={i}><strong>{tension.bold}</strong>{tension.text}</li>
                ))}
              </ul>
            </div>
            <Separator className="my-8" />
            <div className="reality-conclusion">
              <p className="conclusion-text">{t.reality.conclusion1}</p>
              <p className="conclusion-text emphasis">{t.reality.conclusion2}</p>
              <p className="conclusion-text mt-6">{t.reality.conclusion3}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="section-container section-clients">
        <div className="section-bg-image" style={{ backgroundImage: `url(/images/coaching/coaching-agro.jpg)` }}></div>
        <div className="section-content">
          <div className="section-label">{t.clients.label}</div>
          <h2 className="section-title">{t.clients.title}</h2>
          {(t.clients.intro.pre || t.clients.intro.bold) && (
            <p className="section-intro">
              {t.clients.intro.pre}<strong>{t.clients.intro.bold}</strong>{t.clients.intro.post}<strong>{t.clients.intro.bold2}</strong>{t.clients.intro.post2}
            </p>
          )}
          <div className="clients-grid">
            {t.clients.cards.map((card, i) => {
              const Icon = clientIcons[i];
              return (
                <Card className="client-card" key={i}>
                  <div className="client-icon">
                    <Icon size={32} />
                  </div>
                  <h3 className="client-title">{card}</h3>
                </Card>
              );
            })}
          </div>
          <div className="clients-common">
            <p className="common-label">{t.clients.commonLabel}</p>
            <p className="common-text">{t.clients.commonText}</p>
          </div>
        </div>
      </section>

      {/* Results Section - 3 Levels */}
      <section id="results" className="section-container section-results">
        <div className="section-bg-image" style={{ backgroundImage: `url(/images/coaching/coaching-operations.jpg)` }}></div>
        <div className="section-content">
          <div className="section-label">{t.results.label}</div>
          <h2 className="section-title">{t.results.title}</h2>
          {(t.results.intro.pre || t.results.intro.bold1) && (
            <p className="section-intro">
              {t.results.intro.pre}<strong>{t.results.intro.bold1}</strong>{t.results.intro.mid1}<strong>{t.results.intro.bold2}</strong>{t.results.intro.mid2}<strong>{t.results.intro.bold3}</strong>{t.results.intro.post}
            </p>
          )}
          
          <div className="value-levels">
            {t.results.levels.map((level, i) => (
              <Card key={i} className={`value-level-card level-${['vision','execution','performance'][i]}`}>
                <div className="level-header">
                  <div className="level-number">{level.number}</div>
                  <h3 className="level-title">{level.title}</h3>
                </div>
                <div className="level-content">
                  {level.items.map((item, j) => (
                    <div className="level-item" key={j}>
                      <CheckCircle size={20} className="level-icon" />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
          {(t.results.conclusion1 || t.results.conclusion2) && (
            <div className="results-conclusion-block">
              {t.results.conclusion1 && <p className="conclusion-text">{t.results.conclusion1}</p>}
              {t.results.conclusion2 && <p className="conclusion-text emphasis">{t.results.conclusion2}</p>}
            </div>
          )}
        </div>
      </section>

      {/* Framework Section */}
      <section id="framework" className="section-container section-framework">
        <div className="section-content">
          <div className="section-label">{t.framework.label}</div>
          <h2 className="section-title">{t.framework.title}</h2>
          <p className="section-intro">
            <strong>{t.framework.intro.bold1}</strong>{t.framework.intro.text}<strong>{t.framework.intro.bold2}</strong>{t.framework.intro.post}
          </p>
          <div className="framework-content">
            <div className="framework-stats-grid">
              {t.framework.stats.map((stat, i) => {
                const icons = [GraduationCap, Briefcase, BarChart3, Handshake, Award];
                const Icon = icons[i];
                return (
                  <div className="stat-card" key={i}>
                    <Icon className="stat-icon" size={28} />
                    <p className="stat-label">{stat.label}</p>
                    <p className="stat-value">
                      {stat.value && <>{stat.value}<br />{stat.value2}</>}
                      {stat.valuePre && !stat.accent && <>{stat.valuePre}</>}
                      {stat.valuePre && stat.accent && !stat.certName && (
                        <>
                          {i === 1 ? (
                            <>{stat.valuePre}<strong className="accent-number">{stat.accent}</strong></>
                          ) : (
                            <><strong className="accent-number">{stat.accent}</strong>{stat.valuePre}</>
                          )}
                          {stat.detail && <><br/><span className="stat-detail">{stat.detail}</span></>}
                        </>
                      )}
                      {stat.certName && (
                        <>
                          <a 
                            href="https://www.coachingfederation.org/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="icf-link"
                          >
                            <strong>{stat.certName}</strong>
                            <ExternalLink size={16} className="ml-1" />
                          </a>
                          <br/>
                          <span className="stat-detail">{stat.certDetail}</span>
                        </>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Cofondateur Section */}
      <section id="cofondateur" className="section-container section-cofondateur">
        <div className="section-content">
          <h2 className="section-title">{t.cofondateur.title}</h2>
          <div className="cofondateur-grid">
            <a 
              href="https://unipse.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cofondateur-card"
            >
              <div className="logo-container">
                <img src="/images/logos/unipse-logo.png" alt="Unipse" className="company-logo" />
              </div>
              <p className="company-description">{t.cofondateur.unipseDesc}</p>
            </a>
            
            <a 
              href="https://cayus.ai/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cofondateur-card"
            >
              <div className="logo-container">
                <img src="/images/logos/cayus-logo.png" alt="Cayus" className="company-logo" />
              </div>
              <p className="company-description">{t.cofondateur.cayusDesc}</p>
            </a>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="section-container section-vision">
        <div className="section-content">
          <div className="section-label">{t.vision.label}</div>
          <h2 className="section-title-large">{t.vision.title}</h2>
          <div className="vision-content">
            <p className="vision-intro">{t.vision.intro}</p>
            <p className="vision-text">{t.vision.text}</p>
            <div className="vision-list">
              {t.vision.items.map((item, i) => (
                <div className="vision-item" key={i}>{item}</div>
              ))}
            </div>
            <div className="vision-quote">
              <p className="quote-text">{t.vision.quote1}</p>
              <p className="quote-text">{t.vision.quote2}</p>
            </div>
            <div className="vision-belief">
              <p className="belief-text">{t.vision.belief1}</p>
              <p className="belief-text emphasis">{t.vision.belief2}</p>
            </div>
            {(t.vision.footerBold1 || t.vision.footerBold2) && (
              <p className="vision-footer">
                {t.vision.footerPre}<strong>{t.vision.footerBold1}</strong>{t.vision.footerMid}<strong>{t.vision.footerBold2}</strong>{t.vision.footerPost}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Accompagnements Section */}
      <section id="accompagnements" className="section-container section-accompagnements">
        <div className="section-bg-image" style={{ backgroundImage: `url(/images/coaching/coaching-industry.jpg)` }}></div>
        <div className="section-content">
          <div className="section-label">{t.accompagnements.label}</div>
          <h2 className="section-title">{t.accompagnements.title}</h2>
          {(t.accompagnements.intro.pre || t.accompagnements.intro.bold) && (
            <p className="section-intro">
              {t.accompagnements.intro.pre}<strong>{t.accompagnements.intro.bold}</strong>{t.accompagnements.intro.post}
            </p>
          )}
          <div className="services-grid">
            {t.accompagnements.cards.map((card, i) => (
              <Card key={i} className="service-card accompagnement-card">
                {serviceImages[i] && (
                  <div className="service-image-container">
                    <img src={serviceImages[i]} alt={card.title} className="service-image" />
                  </div>
                )}
                <h3 className="service-title">{card.title}</h3>
                <p className="service-description" dangerouslySetInnerHTML={{ __html: card.description }} />
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-container section-testimonials">
        <div className="section-content">
          <div className="section-label">{t.testimonials.label}</div>
          <h2 className="section-title">{t.testimonials.title}</h2>
          <p className="section-intro">{t.testimonials.intro}</p>
          
          <div className="testimonials-modern-grid">
            {t.testimonials.cards.map((card, i) => (
              <div key={i} className={`testimonial-modern-card ${card.featured ? 'testimonial-featured' : ''}`}>
                {card.featured && <div className="featured-badge">{t.testimonials.featuredBadge}</div>}
                <div className="testimonial-header">
                  <div className="quote-circle">
                    <Quote size={28} />
                  </div>
                  <div className="testimonial-meta">
                    <p className="author-name-large">{card.name}</p>
                    <p className="author-role-large">{card.role}</p>
                  </div>
                </div>
                
                <div className="testimonial-body">
                  {card.texts.map((text, j) => (
                    <p key={j} className="testimonial-text-modern" dangerouslySetInnerHTML={{ __html: text }} />
                  ))}
                </div>
                
                <div className="testimonial-footer">
                  <div className="rating-stars">★★★★★</div>
                  {card.featured && (
                    <div className="testimonial-impact">
                      <CheckCircle size={16} />
                      <span>{t.testimonials.measuredResults}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-container section-contact">
        <div className="section-content">
          <h2 className="section-title-large">{t.contact.title}</h2>
          <p className="contact-subtitle">{t.contact.subtitle}</p>
          
          <div className="contact-content">
            <div className="contact-photo-container">
              <img 
                src="/images/loic-blandel-profile.jpg" 
                alt={t.contact.photoAlt}
                className="contact-photo"
              />
            </div>
            
            <div className="contact-info">
              <Card className="contact-card">
                <h3 className="contact-card-title">{t.contact.cardTitle}</h3>
                {t.contact.cardSubtitle && <p className="contact-card-subtitle">{t.contact.cardSubtitle}</p>}
                <div className="contact-details">
                  <div className="contact-detail">
                    <p className="detail-label">{t.contact.emailLabel}</p>
                    <a href="mailto:coach@loicblandel.com" className="detail-value detail-link">
                      coach@loicblandel.com
                    </a>
                  </div>
                  <div className="contact-detail">
                    <p className="detail-label">{t.contact.phoneLabel}</p>
                    <a href="tel:+33666217700" className="detail-value detail-link">
                      +33 6 66 21 77 00
                    </a>
                  </div>
                  <div className="contact-detail">
                    <p className="detail-label">{t.contact.linkedinLabel}</p>
                    <a 
                      href="https://www.linkedin.com/in/loïc-blandel" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="detail-value detail-link"
                    >
                      linkedin.com/in/loïc-blandel
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p className="footer-text">{t.footer.text}</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
